import React, { useEffect, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { Actividad, ActividadNueva, CallCloseBody, DialerItem } from "../../../services/v2/prospects.service";
import { cuandoViene, errorTexto, RESULTADOS_LLAMADA } from "./salesHelpers";
import { FinLlamada, resultadoInicial } from "./telefono/telefono";
import FormCita, { diaLocal, resumenActividad, tituloCita } from "./formCita";

/**
 * Tarjeta de cierre de llamada dentro del marcador (SPEC §4.3; sustituye al modal llamada.tsx solo aquí): cómo terminó,
 * nota y qué sigue, y un solo "Guardar y Siguiente" que manda todo junto (_callClose).
 * Sin respuesta ya no se guarda sola a los 5 s (Claudia/Esteban, 2026-10-15: "no las deja poner nota"): se queda esperando
 * con "No contestó" marcado, para que pongan nota, cierren la actividad de hoy y agenden la siguiente sin volver a su base.
 */
interface Props {
	campusId: number;
	item: DialerItem;
	fin: FinLlamada | null;
	aviso?: string | null;
	auto: boolean;
	plantelId?: number | null;
	motivosPotencial: string[];
	nota: string;
	onNota: (t: string) => void;
	onGuardar: (body: CallCloseBody) => Promise<void>;
	onCancelar: () => void;
}

const SIN_RESPUESTA = ["no_contesto", "buzon", "ocupado", "cortada", "colgo"];
/** Minutos hasta mañana a las 9:00 am (hora local). */
const hastaManana9 = (): number => {
	const objetivo = new Date(diaLocal(1) + "T09:00:00Z").getTime() + 6 * 3600000;
	return Math.max(60, Math.round((objetivo - Date.now()) / 60000));
};
const REINTENTO: { key: string; label: string; min: () => number }[] = [
	{ key: "1h", label: "En 1 h", min: () => 60 }, { key: "3h", label: "En 3 h", min: () => 180 }, { key: "manana", label: "Mañana", min: hastaManana9 },
];
type Sig = { act: ActividadNueva; label: string; key: string };

const CierreLlamada: React.FC<Props> = ({ campusId, item, fin, aviso, auto, plantelId, motivosPotencial, nota, onNota, onGuardar, onCancelar }) => {
	const inicial = fin ? resultadoInicial(fin) : "";
	const [resultado, setResultado] = useState<string>(inicial);
	const [reintento, setReintento] = useState<string>("");
	const [sig, setSig] = useState<Sig | null>(null);
	const [statusId, setStatusId] = useState<number | null>(null);
	const [motivo, setMotivo] = useState<string>("");
	const [form, setForm] = useState<null | "cita" | "seguimiento" | "promesa">(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	// La actividad de hoy (o vencida) de esta persona, para cerrarla aquí mismo con cómo salió la llamada.
	const [actHoy, setActHoy] = useState<Actividad | null>(null);
	const [cerrar, setCerrar] = useState<boolean | null>(null);   // null = la asesora no ha decidido (se cierra si agenda la siguiente)
	useEffect(() => {
		let vivo = true;
		HttpManager.getInstance().prospectsService.actividades(campusId, item.id).then((r) => {
			const a = (r.data.abiertas || []).find((x) => { const v = cuandoViene(x.due_dia || x.due_at); return !!v && (v.tono === "vencido" || v.tono === "hoy"); });
			if (vivo) setActHoy(a || null);
		}).catch(() => { /* sin la actividad, la tarjeta funciona igual */ });
		return () => { vivo = false; };
	}, [campusId, item.id]);

	const sinRespuesta = SIN_RESPUESTA.includes(resultado);
	const listoParaGuardar = !!resultado && !form && !(statusId === 5 && !motivo) && !(statusId === 4 && !sig);

	const guardar = async () => {
		if (!listoParaGuardar || busy) return;
		setBusy(true); setError(null);
		const r = REINTENTO.find((x) => x.key === reintento);
		const body: CallCloseBody = {
			result: resultado,
			note: nota.trim() || undefined,
			durationSec: fin ? fin.durationSec : undefined,
			callId: fin ? fin.callId : undefined,
			campaignContactId: fin?.campaignContactId || undefined,
			laterMinutes: sinRespuesta && r && !sig ? r.min() : undefined,
			cerrarActividadId: actHoy && cierraActividad ? actHoy.id : undefined,
		};
		if (sig) {
			const a = sig.act;
			body.siguiente = { tipo: a.tipo === "cita" ? "cita" : "seguimiento", fecha: a.fecha, at: a.at, hora_exacta: a.hora_exacta, franja: a.franja || null, modalidad: a.modalidad || null, titulo: a.titulo || null };
		}
		if (resultado === "contesto") {
			if (statusId) { body.statusId = statusId; if (statusId === 5) body.motivo = motivo; }
		} else if (sinRespuesta && statusId) { body.statusId = statusId; if (statusId === 5) body.motivo = motivo || "no contestó"; }
		try { await onGuardar(body); }
		catch (e) { setError(errorTexto(e)); setBusy(false); }
	};
	const elegirResultado = (k: string) => { setResultado(k); setReintento(""); if (k !== "contesto") { setStatusId(null); if (sig?.act.tipo === "cita") setSig(null); setForm(null); } };
	// Con 5 "no contestó" seguidos (esta incluida) se sugiere Potencial de un toque, en vez de sacarla sola de la lista.
	const racha = (item.call_noanswer_streak || 0) + 1;
	const seguimientoRapido = (key: string) => {
		if (sig?.key === key) { setSig(null); return; }
		if (key === "2h") {
			const at = new Date(Date.now() + 2 * 3600000);
			at.setSeconds(0, 0);
			setSig({ key, label: "Seguimiento en 2 h", act: { tipo: "llamar", at: at.toISOString(), hora_exacta: true, franja: null } });
		} else setSig({ key, label: "Seguimiento mañana", act: { tipo: "llamar", fecha: diaLocal(1), hora_exacta: false, franja: null } });
		if (statusId === 4 && sig?.act.tipo === "cita") setStatusId(null);
	};
	const ponerStatus = (id: number) => {
		if (statusId === id) { setStatusId(null); return; }
		if (id === 4 && !sig) { setForm("promesa"); return; }
		setStatusId(id); setMotivo("");
	};
	const cita = tituloCita(plantelId);
	const cierraActividad = cerrar !== null ? cerrar : !!sig;
	const vence = actHoy ? cuandoViene(actHoy.due_dia || actHoy.due_at) : null;
	// Qué sigue: seguimiento (y cita solo si contestó). Lo mismo en las dos ramas.
	const seguimiento = (
		<div className="md-grupo">
			<span className="md-grupo-tit">Seguimiento</span>
			<div className="md-chips">
				<button type="button" className={"md-chip" + (sig?.key === "2h" ? " on" : "")} onClick={() => seguimientoRapido("2h")}>En 2 h</button>
				<button type="button" className={"md-chip" + (sig?.key === "manana" ? " on" : "")} onClick={() => seguimientoRapido("manana")}>Mañana</button>
				<button type="button" className={"md-chip" + (sig?.key === "seguimiento" ? " on" : "")} onClick={() => setForm("seguimiento")}>Elegir…</button>
			</div>
		</div>
	);
	const elegido = sig ? (
		<div className="md-elegido">
			<span>{sig.label}</span>
			<button type="button" className="md-link" onClick={() => { if (sig.act.tipo === "cita" && statusId === 4) setStatusId(null); setSig(null); }}>Quitar</button>
		</div>
	) : null;
	const cerrarActividad = actHoy ? (
		<label className="md-cerrar-act">
			<input type="checkbox" checked={cierraActividad} onChange={(e) => setCerrar(e.target.checked)} />
			<span>Cerrar <b>{actHoy.tipoLabel}</b>{actHoy.titulo ? " · " + actHoy.titulo : ""}{vence ? <span className="md-tenue"> · {vence.texto.replace(/^Vencido/, "vencida").replace(/^Hoy/, "hoy")}</span> : null}
				{" "}como <i>{resultado === "contesto" ? "Se hizo" : "No contestó"}</i></span>
		</label>
	) : null;
	const dur = fin && fin.answered ? Math.floor(fin.durationSec / 60) + ":" + String(fin.durationSec % 60).padStart(2, "0") : null;

	return (
		<div className="md-cierre" onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); guardar(); } }}>
			<div className="md-cierre-cab">
				<b>¿Cómo terminó?</b>
				<span className="md-tenue">{fin ? (dur ? "duró " + dur + " · queda grabada" : "no la contestaron") : "registro a mano"}</span>
			</div>
			{aviso ? <div className="md-aviso">{aviso}</div> : null}
			<div className="md-chips" role="group" aria-label="Cómo terminó">
				{RESULTADOS_LLAMADA.map((r) => (
					<button key={r.key} type="button" className={"md-chip" + (resultado === r.key ? " on" : "")} aria-pressed={resultado === r.key} onClick={() => elegirResultado(r.key)}>
						{r.key === "equivocado" ? "Equivocado" : r.label}</button>
				))}
			</div>
			<label className="md-nota">
				<span>Nota</span>
				<textarea rows={2} value={nota} onChange={(e) => onNota(e.target.value)} placeholder="Qué dijo, qué quedó" />
			</label>

			{form ? (
					<div className="md-form-cita">
						<FormCita plantelId={plantelId} modo={form === "seguimiento" ? "seguimiento" : "cita"} permitirSeguimiento={form === "promesa"}
							aviso={form === "promesa" ? "Una Promesa lleva fecha: la cita o el seguimiento." : null} textoBoton="Listo"
							onCancelar={() => setForm(null)}
							onListo={(a) => {
								const esCita = a.tipo === "cita";
								setSig({ key: form, act: a, label: (esCita ? cita : "Seguimiento") + " " + resumenActividad(a) });
								// La cita pone Promesa sola; "Promesa" con solo seguimiento también.
								if (form === "promesa" || (esCita && item.status_id !== 4)) setStatusId(4);
								setForm(null);
							}} />
					</div>
			) : resultado === "contesto" ? (
					<div className="md-sigue">
						{seguimiento}
						<div className="md-grupo">
							<span className="md-grupo-tit">Cita</span>
							<div className="md-chips">
								<button type="button" className={"md-chip cita" + (sig?.act.tipo === "cita" ? " on" : "")} onClick={() => setForm("cita")}>{cita}</button>
							</div>
						</div>
						<div className="md-grupo">
							<span className="md-grupo-tit">Status</span>
							<div className="md-chips">
								<button type="button" className={"md-chip" + (statusId === 3 ? " on" : "")} onClick={() => ponerStatus(3)}>Interesado</button>
								<button type="button" className={"md-chip" + (statusId === 4 ? " on" : "")} onClick={() => ponerStatus(4)}>Promesa</button>
								<button type="button" className={"md-chip oro" + (statusId === 5 ? " on" : "")} onClick={() => ponerStatus(5)}>Potencial</button>
								<button type="button" className={"md-chip oro" + (statusId === 7 ? " on" : "")} onClick={() => ponerStatus(7)}>No Contactar</button>
							</div>
							{statusId === 5 ? (
								<select className="md-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo de Potencial" autoFocus>
									<option value="">¿Por qué Potencial?</option>
									{motivosPotencial.map((m) => <option key={m} value={m}>{m}</option>)}
								</select>
							) : null}
						</div>
						{elegido}
						{cerrarActividad}
					</div>
			) : sinRespuesta ? (
				<div className="md-sigue">
				{seguimiento}
				{elegido}
				{!sig ? (
				<div className="md-grupo">
					<span className="md-grupo-tit">Volver a intentar</span>
					<div className="md-chips">
						{REINTENTO.map((r) => <button key={r.key} type="button" className={"md-chip" + (reintento === r.key ? " on" : "")} onClick={() => setReintento(reintento === r.key ? "" : r.key)}>{r.label}</button>)}
					</div>
				</div>
				) : null}
				{/* Status también sin respuesta (Esteban, 2026-10-15); Promesa no: sin hablar con el cliente no hay promesa. */}
				<div className="md-grupo">
					<span className="md-grupo-tit">Status{racha >= 5 ? <span className="md-oro"> · van {racha} sin contestar</span> : null}</span>
					<div className="md-chips">
						<button type="button" className={"md-chip" + (statusId === 3 ? " on" : "")} onClick={() => ponerStatus(3)}>Interesado</button>
						<button type="button" className={"md-chip oro" + (statusId === 5 ? " on" : "")} onClick={() => { ponerStatus(5); if (statusId !== 5) setMotivo("no contestó"); }}>Potencial</button>
						<button type="button" className={"md-chip oro" + (statusId === 7 ? " on" : "")} onClick={() => ponerStatus(7)}>No Contactar</button>
					</div>
					{statusId === 5 ? (
						<select className="md-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo de Potencial">
							{motivosPotencial.includes("no contestó") ? null : <option value="no contestó">No contestó</option>}
							{motivosPotencial.map((m) => <option key={m} value={m}>{m}</option>)}
						</select>
					) : null}
				</div>
				{cerrarActividad}
				</div>
			) : null}

			{error ? <div className="md-aviso" role="alert">{error}</div> : null}
			<div className="md-cierre-pie">
				<button type="button" className="md-btn" onClick={onCancelar} disabled={busy}>Cancelar</button>
				<button type="button" className="md-btn pri" disabled={!listoParaGuardar || busy} onClick={guardar} title="Ctrl + Enter">
					{busy ? "Guardando…" : auto ? "Guardar y Siguiente" : "Guardar"}
				</button>
			</div>
		</div>
	);
};

export default CierreLlamada;
